import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ReportUser from '../components/ReportUser';
import BlockUser from '../components/BlockUser';
import EmbeddedChat from '../components/EmbeddedChat';
import Navbar from '../components/Navbar';

function Connections() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('connections'); // connections, received, sent
  const [connections, setConnections] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [showStarters, setShowStarters] = useState(null); // { userId, suggestions }

  useEffect(() => {
    fetchAllData();

    // Poll for updates every 15 seconds so disclosure requests appear without refresh
    const interval = setInterval(() => {
      fetchAllData(true);
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab !== 'connections') {
      fetchData();
    }
  }, [activeTab]);

  const fetchAllData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setMessage('Not authenticated. Please login again.');
        navigate('/login');
        return;
      }
      
      // Fetch all data at once for counts
      const [connectionsRes, receivedRes, sentRes] = await Promise.all([
        axios.get('/api/connections', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/connections/requests/received', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/connections/requests/sent', { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Only update state if data actually changed (prevents unnecessary re-renders)
      const newConnections = connectionsRes.data.connections;
      const newReceived = receivedRes.data.requests;
      const newSent = sentRes.data.requests;
      
      if (JSON.stringify(newConnections) !== JSON.stringify(connections)) {
        setConnections(newConnections);
      }
      if (JSON.stringify(newReceived) !== JSON.stringify(receivedRequests)) {
        setReceivedRequests(newReceived);
      }
      if (JSON.stringify(newSent) !== JSON.stringify(sentRequests)) {
        setSentRequests(newSent);
      }
    } catch (error) {
      if (!silent) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          setMessage('Session expired. Please login again.');
          setTimeout(() => navigate('/login'), 2000);
        } else {
          setMessage(`Error: ${error.response?.data?.error || error.message}`);
        }
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setMessage(''); // Clear previous messages
    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setMessage('Not authenticated. Please login again.');
        navigate('/login');
        return;
      }
      
      if (activeTab === 'connections') {
        console.log('📞 Fetching connections...');
        const response = await axios.get('/api/connections', {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Connections response:', response.data);
        setConnections(response.data.connections);
      } else if (activeTab === 'received') {
        console.log('📞 Fetching received requests...');
        const response = await axios.get('/api/connections/requests/received', {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Received requests response:', response.data);
        setReceivedRequests(response.data.requests);
      } else if (activeTab === 'sent') {
        console.log('📞 Fetching sent requests...');
        const response = await axios.get('/api/connections/requests/sent', {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Sent requests response:', response.data);
        setSentRequests(response.data.requests);
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.response?.status === 401) {
        setMessage('Session expired. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMessage(`Error: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/connections/requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Connection accepted! 🎉');
      fetchAllData(); // Refresh all counts

      // Fetch conversation starters for the new connection
      const request = receivedRequests.find(r => r.request_id === requestId);
      if (request) {
        try {
          const startersRes = await axios.get(`/api/connections/intro-suggestions/${request.from_user_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (startersRes.data.suggestions?.length > 0) {
            setShowStarters({ userId: request.from_user_id, suggestions: startersRes.data.suggestions });
          }
        } catch (e) { /* starters are optional */ }
      }
    } catch (error) {
      setMessage('Failed to accept request');
    }
  };

  const handleDecline = async (requestId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/connections/requests/${requestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Request declined');
      fetchAllData(); // Refresh all counts
    } catch (error) {
      setMessage('Failed to decline request');
    }
  };

  const handleLevelUp = async (connectionId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`/api/connections/${connectionId}/level-up`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.mutualConsent) {
        setMessage('🎉 Disclosure level increased! Both of you agreed.');
      } else if (response.data.pending) {
        setMessage('✅ Request sent! Waiting for them to agree.');
      } else {
        setMessage('Disclosure level increased!');
      }
      
      fetchData();
    } catch (error) {
      if (error.response?.data?.pending) {
        setMessage('You already have a pending request for this level.');
      } else {
        setMessage(error.response?.data?.error || 'Failed to update');
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fff3cd', color: '#856404', text: '⏳ Pending' },
      accepted: { bg: '#d4edda', color: '#155724', text: '✅ Accepted' },
      declined: { bg: '#f8d7da', color: '#721c24', text: '❌ Declined' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '14px',
        fontWeight: '500'
      }}>
        {style.text}
      </span>
    );
  };

  const getLevelText = (level) => {
    switch(level) {
      case 0: return 'Anonymous (Location only)';
      case 1: return 'Names & Photos';
      case 2: return 'Full Profile (Profession, Hometown)';
      case 3: return 'Contact Info (Phone, Email)';
      default: return 'Unknown';
    }
  };

  return (
    <div>
      <Navbar />

      <div className="container" style={{ position: 'relative' }}>
        <div className="card">
          <h2>Your connections</h2>
          
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setActiveTab('connections')}
              className={activeTab === 'connections' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Friends {connections.length}
            </button>
            <button 
              onClick={() => setActiveTab('received')}
              className={activeTab === 'received' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Received {receivedRequests.length}
            </button>
            <button 
              onClick={() => setActiveTab('sent')}
              className={activeTab === 'sent' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Sent {sentRequests.length}
            </button>
          </div>
        </div>

        {message && (
          <div className="card" style={{
            backgroundColor: message.includes('accepted') || message.includes('increased') ? '#d4edda' : '#f8d7da',
            color: message.includes('accepted') || message.includes('increased') ? '#155724' : '#721c24'
          }}>
            {message}
          </div>
        )}

        {/* Conversation Starters — shown after accepting a connection */}
        {showStarters && (
          <div className="card" style={{ background: '#f5f3ff', border: '1px solid #ede9fe' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#4c1d95' }}>💬 Start the conversation!</h3>
              <button onClick={() => setShowStarters(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9ca3af' }}>✕</button>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              You just connected! Here are some ways to break the ice:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {showStarters.suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setChatUser({ userId: showStarters.userId });
                    setChatOpen(true);
                    setShowStarters(null);
                  }}
                  style={{
                    textAlign: 'left', padding: '10px 14px', background: 'white', border: '1px solid #e5e7eb',
                    borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151', lineHeight: '1.4'
                  }}
                >
                  "{suggestion}"
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setChatUser({ userId: showStarters.userId });
                setChatOpen(true);
                setShowStarters(null);
              }}
              style={{ marginTop: '12px', padding: '10px 20px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', width: '100%' }}
            >
              Open Chat →
            </button>
          </div>
        )}

        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }} aria-hidden="true">⏳</div>
            Loading...
          </div>
        ) : activeTab === 'connections' ? (
            connections.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                <img 
                  src="/images/undraw_connected_0xor.svg" 
                  alt="" 
                  style={{ maxWidth: '180px', width: '100%', margin: '0 auto 20px', display: 'block', opacity: 0.8 }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <p style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>No connections yet</p>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                  Find people who share your experience and send a connection request.
                </p>
                <a href="/discovery" className="btn btn-primary" style={{ display: 'inline-block' }}>Find your people →</a>
              </div>
            ) : (
              connections.map((conn) => (
                <div key={conn.connection_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'start', gap: '20px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      background: conn.disclosure_level >= 3 && conn.photo_url 
                        ? `url(${conn.photo_url})` 
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      color: 'white'
                    }}>
                      {!(conn.disclosure_level >= 3 && conn.photo_url) && '👤'}
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h3>
                        {conn.disclosure_level >= 1 
                          ? `${conn.first_name} ${conn.disclosure_level >= 2 ? conn.last_name : ''}`
                          : 'Anonymous User'}
                      </h3>
                      
                      <p style={{ color: '#666', fontSize: '14px' }}>
                        Disclosure Level: {getLevelText(conn.disclosure_level)}
                      </p>

                      {conn.shared_identities && conn.shared_identities.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {conn.shared_identities.map((si, i) => (
                            <span key={i} style={{ padding: '3px 10px', backgroundColor: '#ede9fe', color: '#7c3aed', borderRadius: '6px', fontSize: '12px', fontWeight: '500' }}>
                              {si.icon} {si.display_name}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div style={{ marginTop: '12px' }}>
                        <p>📍 {conn.current_city}, {conn.current_state}</p>
                        {conn.disclosure_level >= 2 && conn.hometown_city && (
                          <p>🏠 From {conn.hometown_city}, {conn.hometown_state}</p>
                        )}
                        {conn.disclosure_level >= 2 && conn.profession && (
                          <p>💼 {conn.profession}</p>
                        )}
                        {conn.disclosure_level >= 3 && (
                          <div style={{ 
                            marginTop: '12px', 
                            padding: '12px', 
                            backgroundColor: '#e7f3ff',
                            borderRadius: '6px',
                            border: '1px solid #b3d9ff'
                          }}>
                            <p style={{ fontWeight: '500', color: '#004085', marginBottom: '8px' }}>
                              📧 Contact Information
                            </p>
                            {conn.email ? (
                              <p style={{ marginTop: '4px' }}>
                                ✉️ <a href={`mailto:${conn.email}`} style={{ color: '#0066cc' }}>
                                  {conn.email}
                                </a>
                              </p>
                            ) : (
                              <p style={{ marginTop: '4px', color: '#999' }}>
                                ✉️ No email address
                              </p>
                            )}
                          </div>
                        )}
                        <p style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
                          Connected on {new Date(conn.accepted_at).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Disclosure level controls */}
                      {conn.disclosure_level < 3 && (
                        <div style={{ marginTop: '16px' }}>
                          {conn.pending_disclosure_from_other && (
                            <div style={{
                              padding: '12px',
                              backgroundColor: '#fff3cd',
                              borderRadius: '6px',
                              marginBottom: '12px'
                            }}>
                              <p style={{ color: '#856404', fontWeight: '500' }}>
                                🔔 Wants to reveal more (Level {conn.pending_disclosure_from_other.to_level})
                              </p>
                              <button 
                                onClick={() => handleLevelUp(conn.connection_id)}
                                className="btn btn-primary"
                                style={{ marginTop: '8px' }}
                              >
                                Agree & Reveal More
                              </button>
                            </div>
                          )}
                          
                          {conn.pending_disclosure_from_me ? (
                            <div style={{
                              padding: '12px',
                              backgroundColor: '#e7f3ff',
                              borderRadius: '6px',
                              color: '#004085'
                            }}>
                              <p>⏳ Waiting for them to agree to Level {conn.pending_disclosure_from_me.to_level}</p>
                            </div>
                          ) : !conn.pending_disclosure_from_other && (
                            <button 
                              onClick={() => handleLevelUp(conn.connection_id)}
                              className="btn btn-primary"
                            >
                              Request Level {conn.disclosure_level + 1}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Message, Report and Block Options */}
                      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => {
                            setChatUser({
                              id: conn.connected_user_id,
                              name: conn.disclosure_level >= 1 ? `${conn.first_name} ${conn.disclosure_level >= 2 ? conn.last_name : ''}` : 'Anonymous User'
                            });
                            setChatOpen(true);
                          }}
                          className="btn btn-primary"
                          style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '6px' 
                          }}
                        >
                          Say hi
                        </button>
                        <ReportUser 
                          userId={conn.connected_user_id} 
                          userName={conn.disclosure_level >= 1 ? `${conn.first_name} ${conn.disclosure_level >= 2 ? conn.last_name : ''}` : 'Anonymous User'}
                          onReport={() => {
                            setMessage('Report submitted successfully');
                            setTimeout(() => setMessage(''), 3000);
                          }}
                        />
                        <BlockUser 
                          userId={conn.connected_user_id} 
                          userName={conn.disclosure_level >= 1 ? `${conn.first_name} ${conn.disclosure_level >= 2 ? conn.last_name : ''}` : 'Anonymous User'}
                          onBlock={() => {
                            setMessage('User blocked and connection removed');
                            fetchAllData(); // Refresh to remove blocked user
                            setTimeout(() => setMessage(''), 3000);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )
          ) : activeTab === 'received' ? (
            receivedRequests.length === 0 ? (
              <div className="card">
                <p>No requests yet!</p>
              </div>
            ) : (
              receivedRequests.map((request) => (
                <div key={request.request_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'start', gap: '20px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      👤
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h3>Anonymous User</h3>
                      {request.shared_categories && request.shared_categories.length > 0 ? (
                        <p style={{ color: '#7C3AED', fontWeight: '500' }}>
                          ✓ Shared: {request.shared_categories.join(', ')}
                        </p>
                      ) : (
                        <p style={{ color: '#666' }}>✓ Shares your identity</p>
                      )}
                      
                      <div style={{ marginTop: '12px' }}>
                        {request.current_city && (
                          <p>📍 {request.current_city}, {request.current_state}</p>
                        )}
                        {request.hometown_city && (
                          <p>🏠 From {request.hometown_city}, {request.hometown_state}</p>
                        )}
                        {request.profession && (
                          <p>💼 {request.profession}</p>
                        )}
                        {request.interests && request.interests.length > 0 && (
                          <p>⭐ {request.interests.map(i => i.replace(/_/g, ' ')).join(', ')}</p>
                        )}
                      </div>

                      {request.message && (
                        <div style={{ 
                          marginTop: '12px', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          borderRadius: '6px'
                        }}>
                          <p style={{ fontStyle: 'italic' }}>"{request.message}"</p>
                        </div>
                      )}

                      <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => handleAccept(request.request_id)}
                          className="btn btn-primary"
                        >
                          Accept
                        </button>
                        <button 
                          onClick={() => handleDecline(request.request_id)}
                          className="btn btn-secondary"
                        >
                          Decline
                        </button>
                        <ReportUser 
                          userId={request.from_user_id} 
                          userName="Anonymous User"
                          onReport={() => {
                            setMessage('Report submitted successfully');
                            setTimeout(() => setMessage(''), 3000);
                          }}
                        />
                        <BlockUser 
                          userId={request.from_user_id} 
                          userName="Anonymous User"
                          onBlock={() => {
                            setMessage('User blocked successfully');
                            fetchAllData();
                            setTimeout(() => setMessage(''), 3000);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
            // Sent Requests Tab
            sentRequests.length === 0 ? (
              <div className="card">
                <p>No sent requests yet!</p>
                <p style={{ color: '#666' }}>
                  Go <a href="/discovery">find your people</a>
                </p>
              </div>
            ) : (
              sentRequests.map((request) => (
                <div key={request.request_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'start', gap: '20px' }}>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '50%', 
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      👤
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <h3>Anonymous User</h3>
                        {getStatusBadge(request.status)}
                      </div>
                      
                      <p style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
                        Sent on {new Date(request.created_at).toLocaleDateString()} at {new Date(request.created_at).toLocaleTimeString()}
                      </p>

                      {/* Location Information */}
                      <div style={{ marginTop: '12px' }}>
                        {request.current_city && (
                          <p>📍 {request.current_city}, {request.current_state}</p>
                        )}
                        {request.hometown_city && (
                          <p>🏠 From {request.hometown_city}, {request.hometown_state}</p>
                        )}
                        {request.profession && (
                          <p>💼 {request.profession}</p>
                        )}
                        {request.member_since && (
                          <p style={{ color: '#999', fontSize: '14px' }}>
                            Member since {new Date(request.member_since).toLocaleDateString()}
                          </p>
                        )}
                      </div>

                      {request.message && (
                        <div style={{ 
                          marginTop: '12px', 
                          padding: '12px', 
                          backgroundColor: '#f5f5f5',
                          borderRadius: '6px'
                        }}>
                          <p style={{ fontSize: '14px', color: '#666' }}>Your message:</p>
                          <p style={{ fontStyle: 'italic', marginTop: '4px' }}>"{request.message}"</p>
                        </div>
                      )}

                      {request.status === 'pending' && (
                        <p style={{ marginTop: '12px', color: '#856404', fontSize: '14px' }}>
                          ⏳ Waiting for response...
                        </p>
                      )}
                      {request.status === 'accepted' && (
                        <p style={{ marginTop: '12px', color: '#155724', fontSize: '14px' }}>
                          ✅ Request accepted! Check your Connections tab.
                        </p>
                      )}
                      {request.status === 'declined' && (
                        <p style={{ marginTop: '12px', color: '#721c24', fontSize: '14px' }}>
                          ❌ Request was declined.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )
          )}
      </div>

      {/* Inline Chat Panel */}
      {chatOpen && chatUser && (
        <>
          <div 
            className="chat-panel-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
              animation: 'fadeIn 0.2s ease-in'
            }}
            onClick={() => {
              setChatOpen(false);
              setChatUser(null);
            }}
          />
          <div 
            className="chat-panel open"
            style={{
              position: 'fixed',
              right: '0',
              top: '60px',
              bottom: 0,
              width: '400px',
              maxWidth: '90vw',
              backgroundColor: 'white',
              boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <EmbeddedChat 
              userId={chatUser.id} 
              userName={chatUser.name}
              onClose={() => {
                setChatOpen(false);
                setChatUser(null);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default Connections;

